import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  advanceLearningConversationLease,
  buildLearningConversationLeaseKey,
  LEARNING_CONVERSATION_LEASE_MS,
  readLearningConversationLease,
  type LearningConversationLeaseScope,
  writeLearningConversationLease,
} from './learningConversationLease';

export function useLearningConversationLease(scope?: LearningConversationLeaseScope) {
  const scopeKey = scope ? buildLearningConversationLeaseKey(scope) : undefined;
  const [leaseState, setLeaseState] = useState<{
    scopeKey?: string;
    conversationId?: string;
    startedAt?: number;
    generation: number;
  }>({ generation: 0 });
  const conversationId = leaseState.scopeKey === scopeKey
    ? leaseState.conversationId
    : undefined;
  const activeGeneration = leaseState.scopeKey === scopeKey ? leaseState.generation : 0;
  const activeStartedAt = leaseState.scopeKey === scopeKey ? leaseState.startedAt : undefined;
  const currentVersionRef = useRef({ scopeKey, generation: activeGeneration });
  const activeStartedAtRef = useRef(activeStartedAt);
  currentVersionRef.current = { scopeKey, generation: activeGeneration };
  activeStartedAtRef.current = activeStartedAt;

  useLayoutEffect(() => {
    if (!scope || typeof window === 'undefined') {
      setLeaseState({ scopeKey, generation: 0 });
      return;
    }

    try {
      const lease = readLearningConversationLease(window.localStorage, scope);
      setLeaseState({
        scopeKey,
        conversationId: lease?.conversationId,
        startedAt: lease?.startedAt,
        generation: 0,
      });
    } catch {
      setLeaseState({ scopeKey, generation: 0 });
    }
  }, [scope, scopeKey]);

  useEffect(() => {
    if (
      !scope
      || leaseState.scopeKey !== scopeKey
      || leaseState.startedAt === undefined
      || typeof window === 'undefined'
    ) return;

    const startedAt = leaseState.startedAt;
    const expiresIn = Math.max(
      0,
      startedAt + LEARNING_CONVERSATION_LEASE_MS - Date.now(),
    );
    const timer = window.setTimeout(() => {
      try {
        readLearningConversationLease(window.localStorage, scope, Date.now());
      } catch {
        // Browser storage is optional; the in-memory boundary still expires.
      }
      setLeaseState((current) => (
        current.scopeKey === scopeKey && current.startedAt === startedAt
          ? { scopeKey, generation: current.generation + 1 }
          : current
      ));
    }, expiresIn);

    return () => window.clearTimeout(timer);
  }, [leaseState.scopeKey, leaseState.startedAt, scope, scopeKey]);

  const rememberConversation = useCallback((nextConversationId: string) => {
    if (!scope || typeof window === 'undefined') return false;
    if (
      currentVersionRef.current.scopeKey !== scopeKey
      || currentVersionRef.current.generation !== activeGeneration
    ) return false;

    try {
      const expectedStartedAt = activeStartedAtRef.current;
      const lease = expectedStartedAt === undefined
        ? writeLearningConversationLease(window.localStorage, scope, nextConversationId)
        : advanceLearningConversationLease(
            window.localStorage,
            scope,
            nextConversationId,
            expectedStartedAt,
          );
      if (!lease) return false;
      setLeaseState((current) => ({
        scopeKey,
        conversationId: lease.conversationId,
        startedAt: lease.startedAt,
        generation: current.scopeKey === scopeKey ? current.generation : 0,
      }));
      return true;
    } catch {
      // Central conversation persistence remains authoritative when browser storage is unavailable.
      return false;
    }
  }, [activeGeneration, scope, scopeKey]);

  const startNewConversation = useCallback(() => {
    if (!scope || !scopeKey || typeof window === 'undefined') return false;
    if (currentVersionRef.current.scopeKey !== scopeKey) return false;

    try {
      window.localStorage.removeItem(buildLearningConversationLeaseKey(scope));
    } catch {
      // Browser storage is optional; the in-memory boundary still starts a fresh session.
    }
    setLeaseState((current) => (
      current.scopeKey === scopeKey
        ? { scopeKey, generation: current.generation + 1 }
        : current
    ));
    return true;
  }, [scope, scopeKey]);

  const startedAt = leaseState.scopeKey === scopeKey ? leaseState.startedAt : undefined;
  const boundaryKey = scopeKey
    ? `${scopeKey}:${leaseState.scopeKey === scopeKey ? leaseState.generation : 0}`
    : undefined;
  return {
    boundaryKey,
    conversationId,
    rememberConversation,
    startNewConversation,
    scopeKey,
    startedAt,
  };
}
